import {
  EDITABLE_STATUSES,
  PRINTABLE_STATUSES,
  RECEIVABLE_STATUSES,
  SOURCE_TYPE_LABELS,
  SUPPLY_ORDER_STATUS_LABELS,
  SUPPLY_ORDER_STATUSES,
  TERMINAL_STATUSES,
} from '../constants/supplyOrdersConstants';

export const getSupplyOrderApiErrorMessage = (
  error,
  fallbackMessage = 'No se pudo completar la operación'
) => {
  const data = error?.response?.data;

  if (!data) {
    return error?.message || fallbackMessage;
  }

  if (typeof data === 'string') {
    return data;
  }

  const candidates = [data.error, data.detail, data.message];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim().replace(/^Error de validación:\s*/i, '');
    }
  }

  if (error?.response?.status === 500) {
    return (
      candidates.find((c) => typeof c === 'string' && c.trim()) ||
      'Error interno del servidor. Revisá que el depósito, proveedor y artículos sean válidos.'
    );
  }

  return error?.message || fallbackMessage;
};

const trimOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
};

export const normalizeSupplyOrderQuantity = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  return Math.round(number * 1000) / 1000;
};

export const sanitizeSupplyOrderPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;

  const clean = { ...payload };

  if (clean.source_type === 'provider') {
    delete clean.uuid_source_warehouse;
  } else if (clean.source_type === 'warehouse') {
    delete clean.uuid_provider_legal_entity;
  }

  ['delivery_place', 'delivery_contact_name', 'delivery_contact_phone', 'conditions', 'observations'].forEach(
    (field) => {
      const value = trimOrNull(clean[field]);
      if (value) {
        clean[field] = value;
      } else {
        delete clean[field];
      }
    }
  );

  if (clean.required_at) {
    const datePart = String(clean.required_at).split('T')[0];
    if (datePart) {
      clean.required_at = `${datePart}T00:00:00`;
    } else {
      delete clean.required_at;
    }
  }

  if (clean.source_type === 'provider' && clean.provider_delivery_commitment_at) {
    const datePart = String(clean.provider_delivery_commitment_at).split('T')[0];
    if (datePart) {
      clean.provider_delivery_commitment_at = `${datePart}T00:00:00`;
    } else {
      delete clean.provider_delivery_commitment_at;
    }
  } else {
    delete clean.provider_delivery_commitment_at;
  }

  if (Array.isArray(clean.items)) {
    clean.items = clean.items
      .map((item) => {
        if (!item?.uuid_inventory_article) return null;

        const quantity = normalizeSupplyOrderQuantity(item.requested_quantity);
        if (!quantity) return null;

        const cleanItem = {
          uuid_inventory_article: item.uuid_inventory_article,
          requested_quantity: quantity,
        };

        const observations = trimOrNull(item.observations);
        if (observations) cleanItem.observations = observations;

        if (item.uuid_external_order_item) {
          cleanItem.uuid_external_order_item = item.uuid_external_order_item;
        }
        if (item.uuid_stock_transfer_item) {
          cleanItem.uuid_stock_transfer_item = item.uuid_stock_transfer_item;
        }

        return cleanItem;
      })
      .filter(Boolean);
  }

  Object.keys(clean).forEach((key) => {
    if (key === 'items') return;
    if (clean[key] === '' || clean[key] === undefined || clean[key] === null) {
      delete clean[key];
    }
  });

  return clean;
};

export const sanitizeSupplyOrdersFromExternalOrderPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;

  const clean = {
    issue_now: Boolean(payload.issue_now),
    groups: [],
  };

  const destinationWarehouse = trimOrNull(payload.uuid_destination_warehouse);
  if (destinationWarehouse) {
    clean.uuid_destination_warehouse = destinationWarehouse;
  }

  if (Array.isArray(payload.groups)) {
    clean.groups = payload.groups
      .map((group) => {
        if (!group?.source_type) return null;

        const items = (group.items || [])
          .map((item) => {
            const quantity = normalizeSupplyOrderQuantity(item?.quantity);
            if (!item?.uuid_external_order_item || !quantity) return null;
            return {
              uuid_external_order_item: item.uuid_external_order_item,
              quantity,
            };
          })
          .filter(Boolean);

        if (items.length === 0) return null;

        const sanitizedGroup = {
          source_type: group.source_type,
          items,
        };

        if (group.source_type === 'provider' && group.uuid_provider_legal_entity) {
          sanitizedGroup.uuid_provider_legal_entity = group.uuid_provider_legal_entity;
          const commitmentDate = trimOrNull(group.provider_delivery_commitment_at);
          if (commitmentDate) {
            sanitizedGroup.provider_delivery_commitment_at = `${String(commitmentDate).split('T')[0]}T00:00:00`;
          }
        } else if (group.source_type === 'warehouse' && group.uuid_source_warehouse) {
          sanitizedGroup.uuid_source_warehouse = group.uuid_source_warehouse;
        }

        return sanitizedGroup;
      })
      .filter(Boolean);
  }

  return clean;
};

export const formatSupplyOrderStatus = (status) =>
  SUPPLY_ORDER_STATUS_LABELS[status] || status || 'Sin estado';

export const formatSourceType = (sourceType) =>
  SOURCE_TYPE_LABELS[sourceType] || sourceType || '-';

export const formatSupplyOrderDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatSupplyOrderQuantity = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  return number.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

export const getProviderDisplayLabel = (provider) => {
  if (!provider) return '';
  return (
    provider.legal_business_name?.trim() ||
    provider.company?.trim() ||
    provider.cuit?.trim() ||
    provider.uuid ||
    ''
  );
};

export const getProviderLabel = (providers, uuid) => {
  if (!uuid) return '';
  const provider = providers.find((item) => item.uuid === uuid);
  return getProviderDisplayLabel(provider);
};

export const getWarehouseLabel = (warehouses, uuid) => {
  if (!uuid) return '';
  const warehouse = warehouses.find((item) => item.uuid === uuid);
  if (!warehouse) return '';
  return warehouse.name || warehouse.code || '';
};

export const getExternalOrderLabel = (orders, uuid) => {
  if (!uuid) return '';
  const order = orders.find((item) => item.uuid === uuid);
  if (!order) return '';
  return order.order_number ? `Pedido #${order.order_number}` : uuid;
};

export const buildProvidersByUuid = (providers = []) =>
  providers.reduce((acc, provider) => {
    if (provider?.uuid) {
      acc[provider.uuid] = provider;
    }
    return acc;
  }, {});

export const buildWarehousesByUuid = (warehouses = []) =>
  warehouses.reduce((acc, warehouse) => {
    if (warehouse?.uuid) {
      acc[warehouse.uuid] = warehouse;
    }
    return acc;
  }, {});

export const buildExternalOrdersByUuid = (orders = []) =>
  orders.reduce((acc, order) => {
    if (order?.uuid) {
      acc[order.uuid] = order;
    }
    return acc;
  }, {});

export const mapSupplyOrderToDisplay = (
  order,
  { providersByUuid = {}, warehousesByUuid = {}, externalOrdersByUuid = {} } = {}
) => {
  if (!order) return null;

  const provider =
    providersByUuid[order.uuid_provider_legal_entity] ||
    (order.provider_name_snapshot
      ? { legal_business_name: order.provider_name_snapshot }
      : null);

  const sourceWarehouse =
    warehousesByUuid[order.uuid_source_warehouse] ||
    (order.source_warehouse_name_snapshot
      ? { name: order.source_warehouse_name_snapshot }
      : null);

  const destinationWarehouse =
    warehousesByUuid[order.uuid_destination_warehouse] ||
    (order.destination_warehouse_name_snapshot
      ? { name: order.destination_warehouse_name_snapshot }
      : null);

  const externalOrder = externalOrdersByUuid[order.uuid_external_order];

  const sourceLabel =
    order.source_type === 'provider'
      ? getProviderDisplayLabel(provider) || order.provider_name_snapshot || '-'
      : sourceWarehouse?.name || order.source_warehouse_name_snapshot || '-';

  return {
    ...order,
    number: order.order_code || (order.status === 'draft' ? 'Borrador' : '-'),
    source_type_label: formatSourceType(order.source_type),
    source_label: sourceLabel,
    destination_label:
      destinationWarehouse?.name || order.destination_warehouse_name_snapshot || '-',
    order_date_label: formatSupplyOrderDate(order.order_date),
    provider_delivery_commitment_at_label: formatSupplyOrderDate(
      order.provider_delivery_commitment_at
    ),
    status_label: formatSupplyOrderStatus(order.status),
    external_order_label: externalOrder?.order_number
      ? `#${externalOrder.order_number}`
      : order.uuid_external_order
        ? order.uuid_external_order.slice(0, 8)
        : '-',
  };
};

export const buildCreateSupplyOrderPayload = (formState) => {
  const {
    source_type,
    uuid_provider_legal_entity,
    uuid_source_warehouse,
    uuid_destination_warehouse,
    required_at,
    provider_delivery_commitment_at,
    delivery_place,
    delivery_contact_name,
    delivery_contact_phone,
    conditions,
    observations,
    materials = [],
  } = formState;

  if (!uuid_destination_warehouse) return null;

  if (source_type === 'provider' && !uuid_provider_legal_entity) return null;
  if (source_type === 'warehouse' && !uuid_source_warehouse) return null;

  const validMaterials = materials.filter((item) => {
    if (!item?.materialId) return false;
    return normalizeSupplyOrderQuantity(item.quantity) !== null;
  });

  if (validMaterials.length === 0) return null;

  const payload = {
    source_type,
    uuid_destination_warehouse,
    items: validMaterials.map((item) => {
      const cleanItem = {
        uuid_inventory_article: item.materialId,
        requested_quantity: normalizeSupplyOrderQuantity(item.quantity),
      };

      const itemObservations = trimOrNull(item.observations);
      if (itemObservations) cleanItem.observations = itemObservations;

      return cleanItem;
    }),
  };

  if (source_type === 'provider') {
    payload.uuid_provider_legal_entity = uuid_provider_legal_entity;
  } else {
    payload.uuid_source_warehouse = uuid_source_warehouse;
  }

  const trimmedDeliveryPlace = trimOrNull(delivery_place);
  if (trimmedDeliveryPlace) payload.delivery_place = trimmedDeliveryPlace;

  const trimmedContactName = trimOrNull(delivery_contact_name);
  if (trimmedContactName) payload.delivery_contact_name = trimmedContactName;

  const trimmedContactPhone = trimOrNull(delivery_contact_phone);
  if (trimmedContactPhone) payload.delivery_contact_phone = trimmedContactPhone;

  const trimmedConditions = trimOrNull(conditions);
  if (trimmedConditions) payload.conditions = trimmedConditions;

  const trimmedObservations = trimOrNull(observations);
  if (trimmedObservations) payload.observations = trimmedObservations;

  if (required_at) {
    payload.required_at = `${String(required_at).split('T')[0]}T00:00:00`;
  }

  if (source_type === 'provider' && provider_delivery_commitment_at) {
    payload.provider_delivery_commitment_at = `${String(provider_delivery_commitment_at).split('T')[0]}T00:00:00`;
  }

  return sanitizeSupplyOrderPayload(payload);
};

export const buildUpdateSupplyOrderPayload = (formState) =>
  buildCreateSupplyOrderPayload(formState);

export const mapSupplyOrderToFormState = (order) => {
  if (!order) return null;

  const requiredAt = order.required_at
    ? order.required_at.split('T')[0]
    : '';
  const providerDeliveryCommitmentAt = order.provider_delivery_commitment_at
    ? order.provider_delivery_commitment_at.split('T')[0]
    : '';

  return {
    uuid: order.uuid,
    source_type: order.source_type,
    uuid_provider_legal_entity: order.uuid_provider_legal_entity || '',
    uuid_source_warehouse: order.uuid_source_warehouse || '',
    uuid_destination_warehouse: order.uuid_destination_warehouse || '',
    required_at: requiredAt,
    provider_delivery_commitment_at: providerDeliveryCommitmentAt,
    delivery_place: order.delivery_place || '',
    delivery_contact_name: order.delivery_contact_name || '',
    delivery_contact_phone: order.delivery_contact_phone || '',
    conditions: order.conditions || '',
    observations: order.observations || '',
    materials: (order.items || []).map((item) => ({
      materialId: item.uuid_inventory_article,
      code: item.article_code_snapshot,
      name: item.article_name_snapshot,
      unit: item.unit_name_snapshot || '',
      quantity: item.requested_quantity,
      observations: item.observations || '',
    })),
  };
};

export const canEditSupplyOrder = (order) =>
  order && EDITABLE_STATUSES.includes(order.status);

export const canIssueSupplyOrder = (order) =>
  order?.status === SUPPLY_ORDER_STATUSES.DRAFT;

export const canMarkSupplyOrderSent = (order) =>
  order?.status === SUPPLY_ORDER_STATUSES.ISSUED;

export const canAcknowledgeSupplyOrder = (order) =>
  order?.status === SUPPLY_ORDER_STATUSES.SENT;

export const canReceiveSupplyOrder = (order) =>
  order && RECEIVABLE_STATUSES.includes(order.status);

export const canCancelSupplyOrder = (order) =>
  order && !TERMINAL_STATUSES.includes(order.status);

export const canCloseSupplyOrder = (order) =>
  order &&
  [
    SUPPLY_ORDER_STATUSES.ISSUED,
    SUPPLY_ORDER_STATUSES.SENT,
    SUPPLY_ORDER_STATUSES.ACKNOWLEDGED,
    SUPPLY_ORDER_STATUSES.PARTIALLY_RECEIVED,
  ].includes(order.status);

export const canPrintSupplyOrder = (order) =>
  order && PRINTABLE_STATUSES.includes(order.status);

export const buildReceiveSupplyOrderPayload = (formState, items = []) => {
  const receiptItems = items
    .map((item) => {
      const quantity = Number(item.receiveQuantity);
      if (!quantity || quantity <= 0) return null;

      const pending = Number(item.pending_quantity) || 0;
      if (quantity > pending) return null;

      return {
        uuid_supply_order_item: item.uuid,
        received_quantity: quantity,
        missing_quantity: 0,
        rejected_quantity: 0,
      };
    })
    .filter(Boolean);

  if (receiptItems.length === 0) return null;

  const payload = { items: receiptItems };

  if (formState.received_at) {
    payload.received_at = `${formState.received_at}T00:00:00`;
  }
  if (formState.supplier_document_type) {
    payload.supplier_document_type = formState.supplier_document_type;
  }
  if (formState.supplier_document_number) {
    payload.supplier_document_number = formState.supplier_document_number;
  }
  if (formState.carrier_name) payload.carrier_name = formState.carrier_name;
  if (formState.notes) payload.notes = formState.notes;

  return payload;
};

export const buildGroupsFromExternalOrderItems = (rows) => {
  const groupsMap = new Map();

  rows.forEach((row) => {
    if (!row.selected) return;

    const quantity = normalizeSupplyOrderQuantity(row.quantity);
    if (!quantity) return;

    const groupKey =
      row.source_type === 'provider'
        ? `provider:${row.uuid_provider_legal_entity}:${row.provider_delivery_commitment_at || ''}`
        : `warehouse:${row.uuid_source_warehouse}`;

    if (!groupsMap.has(groupKey)) {
      groupsMap.set(groupKey, {
        source_type: row.source_type,
        uuid_provider_legal_entity:
          row.source_type === 'provider' ? row.uuid_provider_legal_entity : undefined,
        uuid_source_warehouse:
          row.source_type === 'warehouse' ? row.uuid_source_warehouse : undefined,
        provider_delivery_commitment_at:
          row.source_type === 'provider' ? row.provider_delivery_commitment_at || undefined : undefined,
        items: [],
      });
    }

    groupsMap.get(groupKey).items.push({
      uuid_external_order_item: row.uuid_external_order_item,
      quantity,
    });
  });

  return Array.from(groupsMap.values());
};

export const canGenerateSupplyOrdersFromExternalOrder = (order) => {
  if (!order) return false;

  const blockedStatuses = ['pending_authorization', 'cancelled', 'consumed', 'failed'];
  if (blockedStatuses.includes(order.status)) return false;

  return (order.items || []).some((item) => Number(item.missing_quantity) > 0);
};

export const getMissingExternalOrderItems = (order) =>
  (order?.items || []).filter((item) => Number(item.missing_quantity) > 0);
